import React, { useRef, useState, useCallback, useEffect } from 'react';
import { motion, useInView, useScroll, useTransform } from 'framer-motion';
import { Canvas, useFrame } from '@react-three/fiber';
import { Float, RoundedBox } from '@react-three/drei';
import { Code, BookOpen, Music, Shield, Sparkles, Lightbulb } from 'lucide-react';

// Floating 3D Book
function FloatingBook({ position, color, rotationSpeed = 1 }) {
  const mesh = useRef();
  
  useFrame((state) => {
    mesh.current.rotation.y = Math.sin(state.clock.getElapsedTime() * rotationSpeed) * 0.3;
    mesh.current.rotation.z = Math.cos(state.clock.getElapsedTime() * rotationSpeed * 0.5) * 0.1;
  });

  return (
    <Float speed={2} rotationIntensity={0.2} floatIntensity={0.5}>
      <group ref={mesh} position={position}>
        {/* Book cover */}
        <RoundedBox args={[0.8, 1, 0.15]} radius={0.02}>
          <meshStandardMaterial color={color} />
        </RoundedBox>
        {/* Pages */}
        <mesh position={[0.05, 0, 0]}>
          <boxGeometry args={[0.7, 0.9, 0.1]} />
          <meshStandardMaterial color="#F5F0E6" />
        </mesh>
      </group>
    </Float>
  );
}

// Floating 3D Gear/Cog
function FloatingGear({ position, color }) {
  const mesh = useRef();
  
  useFrame((state) => {
    mesh.current.rotation.z = state.clock.getElapsedTime() * 0.5;
  });

  return (
    <Float speed={1.5} rotationIntensity={0.1} floatIntensity={0.4}>
      <mesh ref={mesh} position={position}>
        <torusGeometry args={[0.4, 0.1, 8, 6]} />
        <meshStandardMaterial color={color} metalness={0.6} roughness={0.3} />
      </mesh>
    </Float>
  );
}

// Floating Code Brackets
function FloatingCode({ position }) {
  const mesh = useRef();
  
  useFrame((state) => {
    mesh.current.rotation.y = Math.sin(state.clock.getElapsedTime()) * 0.2;
    mesh.current.position.y = position[1] + Math.sin(state.clock.getElapsedTime() * 1.5) * 0.1;
  });

  return (
    <group ref={mesh} position={position}>
      <Float speed={2} floatIntensity={0.3}>
        {/* Left bracket */}
        <mesh position={[-0.2, 0, 0]}>
          <boxGeometry args={[0.08, 0.5, 0.08]} />
          <meshStandardMaterial color="#E07A5F" />
        </mesh>
        <mesh position={[-0.1, 0.2, 0]}>
          <boxGeometry args={[0.2, 0.08, 0.08]} />
          <meshStandardMaterial color="#E07A5F" />
        </mesh>
        <mesh position={[-0.1, -0.2, 0]}>
          <boxGeometry args={[0.2, 0.08, 0.08]} />
          <meshStandardMaterial color="#E07A5F" />
        </mesh>
        {/* Right bracket */}
        <mesh position={[0.2, 0, 0]}>
          <boxGeometry args={[0.08, 0.5, 0.08]} />
          <meshStandardMaterial color="#E07A5F" />
        </mesh>
        <mesh position={[0.1, 0.2, 0]}>
          <boxGeometry args={[0.2, 0.08, 0.08]} />
          <meshStandardMaterial color="#E07A5F" />
        </mesh>
        <mesh position={[0.1, -0.2, 0]}>
          <boxGeometry args={[0.2, 0.08, 0.08]} />
          <meshStandardMaterial color="#E07A5F" />
        </mesh>
      </Float>
    </group>
  );
}

// Floating Musical Note
function FloatingNote({ position, color }) {
  const mesh = useRef();
  
  useFrame((state) => {
    mesh.current.rotation.z = Math.sin(state.clock.getElapsedTime() * 2) * 0.15;
    mesh.current.position.y = position[1] + Math.sin(state.clock.getElapsedTime() * 1.2) * 0.15;
  });

  return (
    <group ref={mesh} position={position}>
      <Float speed={2.5} floatIntensity={0.4}>
        {/* Note head */}
        <mesh position={[0, -0.15, 0]} rotation={[0, 0, 0.3]}>
          <sphereGeometry args={[0.12, 16, 16]} />
          <meshStandardMaterial color={color} />
        </mesh>
        {/* Note stem */}
        <mesh position={[0.1, 0.15, 0]}>
          <boxGeometry args={[0.03, 0.5, 0.03]} />
          <meshStandardMaterial color={color} />
        </mesh>
        {/* Note flag */}
        <mesh position={[0.18, 0.3, 0]} rotation={[0, 0, -0.3]}>
          <boxGeometry args={[0.15, 0.04, 0.03]} />
          <meshStandardMaterial color={color} />
        </mesh>
      </Float>
    </group>
  );
}

// Floating Lightbulb
function FloatingBulb({ position }) {
  const mesh = useRef();
  const glowRef = useRef();
  
  useFrame((state) => {
    mesh.current.rotation.z = Math.sin(state.clock.getElapsedTime()) * 0.1;
    const glow = 0.3 + Math.sin(state.clock.getElapsedTime() * 3) * 0.1;
    glowRef.current.material.emissiveIntensity = glow;
  });

  return (
    <group ref={mesh} position={position}>
      <Float speed={1.8} floatIntensity={0.5}>
        {/* Bulb */}
        <mesh ref={glowRef}>
          <sphereGeometry args={[0.2, 16, 16]} />
          <meshStandardMaterial color="#F4D03F" emissive="#F4D03F" emissiveIntensity={0.3} />
        </mesh>
        {/* Base */}
        <mesh position={[0, -0.25, 0]}>
          <cylinderGeometry args={[0.08, 0.1, 0.15, 8]} />
          <meshStandardMaterial color="#8B7355" metalness={0.5} />
        </mesh>
      </Float>
    </group>
  );
}

// 3D Scene for About Section
function AboutScene() {
  return (
    <>
      <ambientLight intensity={0.8} />
      <pointLight position={[5, 5, 5]} intensity={0.5} color="#F4D03F" />
      <pointLight position={[-5, -5, 5]} intensity={0.3} color="#E07A5F" />
      
      <FloatingBook position={[-2, 1.5, 0]} color="#3D405B" rotationSpeed={0.8} />
      <FloatingBook position={[2.5, -1, 0]} color="#81B29A" rotationSpeed={1.2} />
      <FloatingGear position={[-2.5, -1.5, 0]} color="#C9A227" />
      <FloatingGear position={[1.5, 2, 0]} color="#E07A5F" />
      <FloatingCode position={[0, -2, 0]} />
      <FloatingNote position={[-1.5, 0.5, 0]} color="#81B29A" />
      <FloatingNote position={[2, 0, 0]} color="#E07A5F" />
      <FloatingBulb position={[0, 1.8, 0]} />
    </>
  );
}

// Animated icon component
function AnimatedIcon({ icon: Icon, delay, color }) {
  return (
    <motion.div
      initial={{ opacity: 0, scale: 0 }}
      whileInView={{ opacity: 1, scale: 1 }}
      viewport={{ once: true }}
      transition={{ delay, type: "spring", stiffness: 200 }}
      className="absolute"
      style={{ color }}
    >
      <Icon size={24} />
    </motion.div>
  );
}

/* ──────────────────────────────────────────────
   3D RUBIK'S CUBE - interactive drag-to-rotate
   ────────────────────────────────────────────── */
const FACE_COLORS = {
  front: '#F2CC8F',   // gold
  back: '#3D405B',    // slate
  right: '#E07A5F',   // terracotta
  left: '#81B29A',    // sage
  top: '#F4F1DE',     // cream
  bottom: '#C4963A',  // ochre
};

const CUBE_SIZE = 180;
const GAP = 4;
const TILE = (CUBE_SIZE - GAP * 4) / 3;
const HALF = CUBE_SIZE / 2;

const FACE_DEFS = [
  { key: 'front',  base: `translateZ(${HALF}px)`,                  color: FACE_COLORS.front,  blast: { x: 0,    y: 0,    z: 200 } },
  { key: 'back',   base: `rotateY(180deg) translateZ(${HALF}px)`,  color: FACE_COLORS.back,   blast: { x: 0,    y: 0,    z: -200 } },
  { key: 'right',  base: `rotateY(90deg) translateZ(${HALF}px)`,   color: FACE_COLORS.right,  blast: { x: 200,  y: 0,    z: 0 } },
  { key: 'left',   base: `rotateY(-90deg) translateZ(${HALF}px)`,  color: FACE_COLORS.left,   blast: { x: -200, y: 0,    z: 0 } },
  { key: 'top',    base: `rotateX(90deg) translateZ(${HALF}px)`,   color: FACE_COLORS.top,    blast: { x: 0,    y: -200, z: 0 } },
  { key: 'bottom', base: `rotateX(-90deg) translateZ(${HALF}px)`,  color: FACE_COLORS.bottom, blast: { x: 0,    y: 200,  z: 0 } },
];

function RubikFace({ transform, color, explode, blast }) {
  const e = explode || 0;
  const fullTransform = `${transform} translate3d(${blast.x * e}px, ${blast.y * e}px, ${blast.z * e}px) rotate(${e * 15}deg)`;
  const tiles = [];
  for (let row = 0; row < 3; row++) {
    for (let col = 0; col < 3; col++) {
      const x = GAP + col * (TILE + GAP);
      const y = GAP + row * (TILE + GAP);
      tiles.push(
        <div
          key={`${row}-${col}`}
          style={{
            position: 'absolute',
            left: x,
            top: y,
            width: TILE,
            height: TILE,
            backgroundColor: color,
            borderRadius: 6,
            boxShadow: 'inset 0 0 0 1px rgba(255,255,255,0.15)',
          }}
        />
      );
    }
  }
  return (
    <div
      style={{
        position: 'absolute',
        width: CUBE_SIZE,
        height: CUBE_SIZE,
        transform: fullTransform,
        backgroundColor: '#1a1a1a',
        borderRadius: 8,
        opacity: 1 - e * 0.3,
        transition: 'opacity 0.1s',
      }}
    >
      {tiles}
    </div>
  );
}

function RubiksCube({ explode: explodeMV }) {
  const [explode, setExplode] = useState(0);

  useEffect(() => {
    if (!explodeMV || typeof explodeMV === 'number') {
      setExplode(explodeMV || 0);
      return;
    }
    setExplode(explodeMV.get());
    const unsubscribe = explodeMV.on('change', (v) => setExplode(v));
    return unsubscribe;
  }, [explodeMV]);

  const [rotation, setRotation] = useState({ x: -25, y: 35 });
  const [dragging, setDragging] = useState(false);
  const lastPos = useRef({ x: 0, y: 0 });
  const autoRef = useRef(null);
  const velocityRef = useRef({ x: 0, y: 0 });

  const handlePointerDown = useCallback((e) => {
    if (explode > 0.1) return;
    setDragging(true);
    lastPos.current = { x: e.clientX, y: e.clientY };
    velocityRef.current = { x: 0, y: 0 };
    e.currentTarget.setPointerCapture(e.pointerId);
  }, [explode]);

  const handlePointerMove = useCallback((e) => {
    if (!dragging) return;
    const dx = e.clientX - lastPos.current.x;
    const dy = e.clientY - lastPos.current.y;
    lastPos.current = { x: e.clientX, y: e.clientY };
    velocityRef.current = { x: dx, y: dy };
    setRotation((prev) => ({
      x: prev.x - dy * 0.5,
      y: prev.y + dx * 0.5,
    }));
  }, [dragging]);

  const handlePointerUp = useCallback(() => {
    setDragging(false);
  }, []);

  useEffect(() => {
    if (dragging) {
      if (autoRef.current) cancelAnimationFrame(autoRef.current);
      return;
    }

    let vx = velocityRef.current.x * 0.5;
    let vy = velocityRef.current.y * 0.5;
    const hasInertia = Math.abs(vx) > 0.5 || Math.abs(vy) > 0.5;

    const tick = () => {
      if (hasInertia && (Math.abs(vx) > 0.1 || Math.abs(vy) > 0.1)) {
        vx *= 0.96;
        vy *= 0.96;
        setRotation((prev) => ({
          x: prev.x - vy,
          y: prev.y + vx,
        }));
      } else {
        setRotation((prev) => ({
          x: prev.x,
          y: prev.y + 0.15,
        }));
      }
      autoRef.current = requestAnimationFrame(tick);
    };
    autoRef.current = requestAnimationFrame(tick);
    return () => { if (autoRef.current) cancelAnimationFrame(autoRef.current); };
  }, [dragging]);

  return (
    <div
      style={{
        perspective: 600,
        width: CUBE_SIZE + 60,
        height: CUBE_SIZE + 60,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        cursor: explode > 0.1 ? 'default' : dragging ? 'grabbing' : 'grab',
        touchAction: 'none',
        userSelect: 'none',
      }}
      onPointerDown={handlePointerDown}
      onPointerMove={handlePointerMove}
      onPointerUp={handlePointerUp}
      onPointerLeave={handlePointerUp}
    >
      <div
        style={{
          width: CUBE_SIZE,
          height: CUBE_SIZE,
          position: 'relative',
          transformStyle: 'preserve-3d',
          transform: `rotateX(${rotation.x}deg) rotateY(${rotation.y}deg)`,
        }}
      >
        {FACE_DEFS.map((f) => (
          <RubikFace key={f.key} transform={f.base} color={f.color} explode={explode} blast={f.blast} />
        ))}
      </div>
    </div>
  );
}

export function AboutSection() {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-100px" });
  const cubeContainerRef = useRef(null);
  const { scrollYProgress: cubeScroll } = useScroll({
    target: cubeContainerRef,
    offset: ['start end', 'end start'],
  });
  const explode = useTransform(cubeScroll, [0.55, 0.85], [0, 1], { clamp: true });
  const textOpacity = useTransform(explode, [0, 0.3], [0.6, 0]);

  return (
    <section 
      id="about"
      data-testid="about-section"
      ref={ref}
      className="relative min-h-screen py-32 px-6 overflow-hidden bg-background"
    >
      {/* 3D Background Canvas - very subtle */}
      <div className="absolute inset-0 z-0 opacity-25 pointer-events-none">
        <Canvas camera={{ position: [0, 0, 8], fov: 50 }}>
          <AboutScene />
        </Canvas>
      </div>

      {/* Floating decorative icons - very subtle */}
      <div className="absolute inset-0 z-0 overflow-hidden pointer-events-none">
        <motion.div
          animate={{ y: [0, -10, 0] }}
          transition={{ duration: 6, repeat: Infinity, ease: "easeInOut" }}
          className="absolute top-20 left-[5%]"
        >
          <Code size={20} className="text-primary/10" />
        </motion.div>
        <motion.div
          animate={{ y: [0, 8, 0] }}
          transition={{ duration: 5, repeat: Infinity, ease: "easeInOut", delay: 0.5 }}
          className="absolute top-40 right-[8%]"
        >
          <BookOpen size={18} className="text-secondary/10" />
        </motion.div>
        <motion.div
          animate={{ y: [0, -8, 0] }}
          transition={{ duration: 7, repeat: Infinity, ease: "easeInOut", delay: 1 }}
          className="absolute bottom-40 left-[12%]"
        >
          <Music size={16} className="text-accent/10" />
        </motion.div>
        <motion.div
          animate={{ y: [0, 10, 0] }}
          transition={{ duration: 6, repeat: Infinity, ease: "easeInOut", delay: 1.5 }}
          className="absolute bottom-32 right-[5%]"
        >
          <Shield size={18} className="text-primary/10" />
        </motion.div>
      </div>

      <div className="max-w-7xl mx-auto relative z-10">
        <div className="grid lg:grid-cols-2 gap-16 items-center">
          {/* Left: Text Content */}
          <motion.div
            initial={{ opacity: 0, x: -50 }}
            animate={isInView ? { opacity: 1, x: 0 } : {}}
            transition={{ duration: 0.8 }}
            className="space-y-8 bg-background/80 backdrop-blur-sm rounded-2xl p-6 -m-6"
          >
            <div className="space-y-4">
              <motion.span 
                className="text-xs font-bold uppercase tracking-[0.3em] text-primary font-body inline-flex items-center gap-2"
                initial={{ opacity: 0 }}
                animate={isInView ? { opacity: 1 } : {}}
                transition={{ delay: 0.2 }}
              >
                What Do I Do?
              </motion.span>
              
              <motion.h2 
                data-testid="about-title"
                className="font-heading text-4xl md:text-5xl lg:text-6xl font-semibold text-foreground leading-tight"
                initial={{ opacity: 0, y: 20 }}
                animate={isInView ? { opacity: 1, y: 0 } : {}}
                transition={{ delay: 0.3, duration: 0.8 }}
              >
                Think of <span className="text-primary italic">curiosity</span> meets{' '}
                <span className="text-secondary italic">structure</span>.
              </motion.h2>
            </div>

            <motion.div
              className="space-y-6 font-body text-lg text-muted-foreground leading-relaxed"
              initial={{ opacity: 0 }}
              animate={isInView ? { opacity: 1 } : {}}
              transition={{ delay: 0.5, duration: 0.8 }}
            >
              <p>
                I love exploring problems and experimenting with action-driven solutions 
                to create a positive impact in the world. I'm curious, explorative, and 
                love talking about what I discover and formulate.
              </p>
              
              <div className="space-y-3 pl-4 border-l-2 border-primary/30">
                <motion.p 
                  className="text-foreground font-medium"
                  initial={{ opacity: 0, x: -20 }}
                  animate={isInView ? { opacity: 1, x: 0 } : {}}
                  transition={{ delay: 0.6 }}
                >
                  <span className="text-primary">Curiosity</span> meets <span className="text-primary">Craft</span>.
                </motion.p>
                <motion.p 
                  className="text-foreground font-medium"
                  initial={{ opacity: 0, x: -20 }}
                  animate={isInView ? { opacity: 1, x: 0 } : {}}
                  transition={{ delay: 0.7 }}
                >
                  <span className="text-secondary">Rabbit holes</span> meets <span className="text-secondary">Real outcomes</span>.
                </motion.p>
                <motion.p 
                  className="text-foreground font-medium"
                  initial={{ opacity: 0, x: -20 }}
                  animate={isInView ? { opacity: 1, x: 0 } : {}}
                  transition={{ delay: 0.8 }}
                >
                  <span className="text-accent">Geek mode</span> meets <span className="text-accent">Real value</span>.
                </motion.p>
              </div>

              <p>
                Whether it's auditing multi-million dollar processes, building automation 
                tools, or exploring the depths of Sanskrit texts, I bring the same rigor 
                and curiosity to everything I do.
              </p>
            </motion.div>
          </motion.div>

          {/* Right: 3D Rubik's Cube */}
          <motion.div
            ref={cubeContainerRef}
            initial={{ opacity: 0, x: 50 }}
            animate={isInView ? { opacity: 1, x: 0 } : {}}
            transition={{ duration: 0.8, delay: 0.3 }}
            className="relative flex items-center justify-center"
          >
            <motion.p
              className="absolute -top-2 right-0 text-muted-foreground text-xs max-w-[140px] text-right leading-relaxed"
              style={{
                fontFamily: "'Georgia', 'Times New Roman', serif",
                fontStyle: 'italic',
                opacity: textOpacity,
              }}
              initial={{ y: 10 }}
              animate={isInView ? { y: 0 } : {}}
              transition={{ duration: 0.8, delay: 1.2 }}
            >
              Are you curious too? Play around with this cube!
            </motion.p>
            <RubiksCube explode={explode} />
          </motion.div>
        </div>
      </div>
    </section>
  );
}
